import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import type { StainFrontPhase, StainWasteKind } from '../../figures/LineFigures.tsx'

export type { StainFrontPhase, StainWasteKind }

/**
 * **염색기 정면도의 «지금»**(eric 2026-10-07 「염색기를 정면도로 수정해줘」 → 「해당 챔버 위치에.. 피펫의 끝이 해당 챔버에 닿게 내리고 올리고
 * 하는 것만 표현해줘.. 피펫 버리기 이런 것은 그냥 텍스트로」).
 *
 * 서버가 피펫 캐리지 좌표를 안 주므로 그림은 **동작 이름**으로 툴이 «챔버 위에 있는지 · 비켜 있는지»를 고른다 — 어셈블러 헤드 · 6축 팔과 같은
 * 도식 규칙이다. 재료는 `module_status` 가 1Hz 로 주는 `current_execution.sequence` · `state` 뿐이고, **어느 챔버인지**는 트레이 뷰
 * `staining.active_wells`(`FocusFigure` 가 넘긴다)다.
 *
 * | 서버 시퀀스(module-core `profiles/staining.yaml` `supported_sequences` 16개 · 2026-10-07 확인) · 상태 | 단계 | 툴 |
 * | --- | --- | --- |
 * | `dispense_reagent` · `aspirate`(챔버에서 빼기) · `mix` · 상태 `DISPENSING` · `PIPETTING` | `well` | 그 챔버 위 · 끝이 닿게 내려옴 |
 * | `draw`(리저버에서 시약 흡입) | `reagent` | 시약 통으로 가서 담근다 |
 * | `expel`(토출) · `discard_tip`(팁 폐기) | `waste` | 폐기통으로 가서 담근다 |
 * | `mount_tip` | `tips` | 왼쪽 팁 랙으로 가서 담근다 |
 * | 상태 `TIP_CHANGE`(동작 이름 없음 — 장착인지 폐기인지 모름) | `aside` | 팁 랙 위 · 올라간 채 — 글자가 말한다 |
 * | `move_to` · `move_z` · 상태 `MOVING` | `move` | 올라간 채 · 가운데 |
 * | `shake` · 상태 `SHAKING` | `shake` | 올라간 채 · 플랫폼 + 트레이 떨림 |
 * | 그 밖(`IDLE` · `INCUBATING` · `slide_tray` · `open_door` …) · 끊긴 화면 | `idle` | 올라간 채 · 가운데 |
 *
 * 🔴 다음 동작을 짐작하거나 시간을 재서 움직이지 않는다 — 프레임이 바뀌면 그때 자리가 바뀐다(CSS transition). 모르는 이름은 상태로만 간다.
 */
const SEQUENCE_PHASE: Readonly<Record<string, StainFrontPhase>> = {
  dispense_reagent: 'well',
  aspirate: 'well',
  mix: 'well',
  mount_tip: 'tips',
  discard_tip: 'waste',
  draw: 'reagent',
  expel: 'waste',
  move_to: 'move',
  move_z: 'move',
  shake: 'shake',
}
const STATE_PHASE: Readonly<Record<string, StainFrontPhase>> = {
  DISPENSING: 'well',
  PIPETTING: 'well',
  TIP_CHANGE: 'aside',
  MOVING: 'move',
  SHAKING: 'shake',
}
/** 액체 방향 — 챔버에 넣는 분주 ↓ · 챔버에서 빼는 흡입 ↑. 혼합은 둘 다라 화살표 없음 */
const SEQUENCE_ARROW: Readonly<Record<string, 'up' | 'down'>> = {
  dispense_reagent: 'down',
  aspirate: 'up',
}
const STATE_ARROW: Readonly<Record<string, 'up' | 'down'>> = {
  DISPENSING: 'down',
  PIPETTING: 'up',
}

function sequenceOf(snapshot: ModuleSnapshot): string | undefined {
  const sequence = snapshot.current_execution?.sequence
  return typeof sequence === 'string' && sequence !== '' ? sequence : undefined
}

/** 자리를 가진 동작(이동 · 대기가 아닌 것) — 분주 · 흡입 · 혼합 · 시약 흡입 · 토출 · 팁 장착 · 폐기 */
const PLACE_PHASES: ReadonlySet<StainFrontPhase> = new Set(['well', 'reagent', 'waste', 'tips'])

/**
 * 🔴 **방금 끝난 동작(`completedAction`)이 먼저다** — 구독 스텝 줄(`batch_progress` `step_complete` 의 `sequence` · 「expel 완료」)의 그 이름.
 * `module_status` 미러(1Hz)의 `current_execution.sequence` 는 0.5~1초짜리 동작(`expel` · `aspirate` · `discard_tip`)을 거의 못 잡아 늘 `move_*`
 * 만 보였다(dev2 2026-10-08 12:30 · 40초 관측에 `data-phase` 가 전부 `move` — 그 사이 스텝 줄에는 aspirate · expel · discard_tip · mount_tip ·
 * draw · dispense_reagent 완료가 다 있었다. eric 「expel 이게 다 있는데 왜 그림으로 표현이 안 돼?」 · 「discard tip 하면 폐기통에 왔다 가야지」 ·
 * 「aspiration 완료 부분 안 가는 그림 있어」). 끝난 동작의 자리에 툴을 세우고(머무름 · `useDwell`), 그 다음 `move_* 완료` 가 오면 `move` 로 간다.
 * 지금 도는 동작이 자리를 가진 것이면(`dispense_reagent` 가 1초 넘게 도는 때) 그것이 더 «지금»이라 앞선다.
 */
export function stainFrontPhase(
  snapshot: ModuleSnapshot | undefined,
  stale: boolean,
  completedAction?: string,
): StainFrontPhase {
  if (snapshot === undefined || stale || snapshot.stale === true) return 'idle'
  const sequence = sequenceOf(snapshot)
  const running = sequence === undefined ? undefined : SEQUENCE_PHASE[sequence]
  if (running !== undefined && PLACE_PHASES.has(running)) return running
  const completed = completedAction === undefined ? undefined : SEQUENCE_PHASE[completedAction]
  if (completed !== undefined) return completed
  if (running !== undefined) return running
  return STATE_PHASE[snapshot.state] ?? 'idle'
}

/** 흡입 ↑ · 분주 ↓ — 챔버 위에서 내려와 있을 때만 그림이 쓴다 */
export function stainFrontArrow(
  snapshot: ModuleSnapshot | undefined,
  stale: boolean,
  completedAction?: string,
): 'up' | 'down' | undefined {
  if (snapshot === undefined || stale || snapshot.stale === true) return undefined
  const sequence = sequenceOf(snapshot)
  const running = sequence === undefined ? undefined : SEQUENCE_ARROW[sequence]
  if (running !== undefined) return running
  /* 방금 끝난 동작의 방향 — 자리와 같은 규칙(`stainFrontPhase`). 끝난 동작이 이동이면 방향 없음 */
  if (completedAction !== undefined && completedAction in SEQUENCE_PHASE)
    return SEQUENCE_ARROW[completedAction]
  if (sequence !== undefined) return undefined
  return STATE_ARROW[snapshot.state]
}

/** 폐기통 자리의 두 동작 — 토출(`expel`) · 팁 버림(`discard_tip`) */
const WASTE_KIND: Readonly<Record<string, StainWasteKind>> = {
  expel: 'liquid',
  discard_tip: 'tip',
}

/**
 * **폐기통에서 무엇을 하나** — 통 안 «부분» 피펫 표현(`BinCue` · eric 2026-10-18 「폐기통에 버릴 때.. 팁을 버리는 느낌」)이 고른다.
 * 자리(`stainFrontPhase`)와 같은 차례 — 지금 도는 동작이 먼저, 없으면 방금 끝난 동작. 둘 다 폐기통 동작이 아니면 `undefined`
 */
export function stainFrontWaste(
  snapshot: ModuleSnapshot | undefined,
  stale: boolean,
  completedAction?: string,
): StainWasteKind | undefined {
  if (snapshot === undefined || stale || snapshot.stale === true) return undefined
  const sequence = sequenceOf(snapshot)
  const running = sequence === undefined ? undefined : WASTE_KIND[sequence]
  if (running !== undefined) return running
  return completedAction === undefined ? undefined : WASTE_KIND[completedAction]
}

/** 시퀀스 이름이 없는 프레임 — 상태가 곧 동작인 것은 같은 글자로(`DISPENSING` → 「시약 넣는 중」) · 그 밖은 상태 이름 */
const STATE_SEQUENCE: Readonly<Record<string, string>> = {
  DISPENSING: 'dispense_reagent',
  PIPETTING: 'aspirate',
  SHAKING: 'shake',
}

/**
 * 그림 아래 한 줄 — 「시약 넣는 중 · 138/313」(서버 시퀀스 이름 · 스텝) · 실행이 없으면 부르는 쪽이 준 `fallback`.
 * 이름은 사전 `enum.stainSequence.*` · 없으면 원값(`enumLabel` 규약). 칸 이름은 그림이 앞에 붙인다(「B2 · 시약 넣는 중 · 138/313」).
 * 🔴 시약 이름은 적지 않는다 — 서버가 분주 중에 주지 않는다(시약 자리 `reagent_well` 뿐 · 이름은 레시피 시약표). 「시약」이라고만 말한다
 */
export function stainFrontStatus(snapshot: ModuleSnapshot | undefined, fallback: string): string {
  const run = snapshot?.current_execution
  if (run == null) return fallback
  const sequence =
    typeof run.sequence === 'string' && run.sequence !== ''
      ? run.sequence
      : STATE_SEQUENCE[snapshot?.state ?? '']
  const name =
    sequence === undefined
      ? enumLabel('enum.moduleState', snapshot?.state)
      : enumLabel('enum.stainSequence', sequence)
  return typeof run.step === 'number' && typeof run.total === 'number'
    ? `${name} · ${t('event.content.stepCount', { step: run.step, total: run.total })}`
    : name
}

/**
 * 툴이 갈 챔버 — 서버가 «하나»를 지목하면(트레이 뷰 `active` 한 칸) 그 칸, 여럿을 한꺼번에 ▶ 로 주면(염색기는 셰이커 위 전부를 `active` 로
 * 낸다 · dev2 실측) **가장 최근 분주 기록의 칸**(`GET /dispense-records` · `currentDispensePass` 첫 키)으로 간다 — 「지금」이 아니라
 * 「직전에 분주한 칸」이라는 서버 사실이다(eric 2026-10-08 「각각의 트레이 위치에 가야 되는데 지금 P1 에만」). 둘 다 없으면 `null`(짐작하지 않는다).
 */
/**
 * **염색기 피펫이 «지금» 들어가 있는 웰** — 시료 카드 챔버의 스포이드 자리(eric 2026-10-12 「분주할 때.. 피펫 넣는 거 표현해줘」).
 * 장비 판 정면도와 같은 두 규칙이다: 자리(`stainFrontPhase`)가 챔버(`well`)일 때만, 칸은 `stainFrontWell`. 그 밖(시약 랙 · 폐기통 · 팁 랙 ·
 * 이동 · 흔들기 · 끊김 · 모듈 모름)은 `null` — 어느 챔버에도 스포이드가 서지 않는다.
 */
export function stainPipetteWell(input: {
  snapshot: ModuleSnapshot | undefined
  stale: boolean
  completedAction?: string | undefined
  cells: readonly { slot: string; verdict?: string | null | undefined }[]
  latestDispensed: string | undefined
}): StainPipetteFrame {
  if (input.snapshot === undefined) return { phase: 'idle', well: null }
  const phase = stainFrontPhase(input.snapshot, input.stale, input.completedAction)
  return {
    phase,
    well: phase === 'well' ? stainFrontWell(input.cells, input.latestDispensed) : null,
  }
}

/** 피펫 한 프레임 — 자리(정면도와 같은 어휘)와 그 자리가 챔버면 칸 */
export interface StainPipetteFrame {
  phase: ReturnType<typeof stainFrontPhase>
  well: string | null
}

/**
 * **다음 피펫 자리** — 🔴 이동(`move`) 중에는 마지막 자리에 머문다(장비 판 정면도의 「이동 중엔 마지막으로 간 자리 위」와 같은 규칙).
 * 분주(`dispense_reagent`)는 1초 안팎이라 이동 프레임이 바로 덮는다 — 머물지 않으면 시료 카드의 스포이드가 깜빡이기만 하고 거의 안 보였다
 * (dev2 2026-10-12 · 3분 관측 0회). 다른 자리(시약 랙 · 폐기통 · 팁 랙)로 가거나 쉬면(대기 · 흔들기 · 끊김) 챔버에서 빠진다.
 */
export function nextPipetteWell(prev: string | null, frame: StainPipetteFrame): string | null {
  if (frame.phase === 'move') return prev
  return frame.phase === 'well' ? frame.well : null
}

export function stainFrontWell(
  cells: readonly { slot: string; verdict?: string | null | undefined }[],
  latestDispensed: string | undefined,
): string | null {
  const active = cells.filter((cell) => cell.verdict === 'active')
  if (active.length === 1) return active[0]?.slot ?? null
  return latestDispensed ?? null
}
